/**
 * Otimiza e redimensiona imagens para extração OCR de alta velocidade e máxima nitidez com a IA.
 * Reduz fotos de 10-15MB para ~150-250KB sem perder legibilidade de fontes pequenas e códigos de barras.
 */
export async function optimizeForAi(file: File): Promise<{ base64: string; mimeType: string }> {
  // Se for PDF, lê direto como base64
  if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const res = reader.result as string;
        resolve(res.includes("base64,") ? res.split("base64,")[1] : res);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    return { base64, mimeType: "application/pdf" };
  }

  // Se for imagem, redimensiona mantendo altíssima nitidez (2400px max) e alta qualidade JPEG (0.94) para OCR de precisão cirúrgica
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      const img = new Image();
      img.onload = () => {
        const MAX_DIM = 2400; // Resolução suficiente para ler códigos de barras de 8pt em fotos
        let width = img.width;
        let height = img.height;

        if (width > MAX_DIM || height > MAX_DIM) {
          if (width > height) {
            height = Math.round((height * MAX_DIM) / width);
            width = MAX_DIM;
          } else {
            width = Math.round((width * MAX_DIM) / height);
            height = MAX_DIM;
          }
        }

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d", { willReadFrequently: false });

        if (ctx) {
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          // Fundo branco para transparências PNG
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
        }

        const optimizedDataUrl = canvas.toDataURL("image/jpeg", 0.94);
        const cleanBase64 = optimizedDataUrl.split("base64,")[1] || optimizedDataUrl;
        resolve({ base64: cleanBase64, mimeType: "image/jpeg" });
      };
      img.onerror = reject;
      img.src = dataUrl;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const compressImage = (file: File, maxSizeMB: number = 2): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/") && !file.name.match(/\.(jpe?g|png|webp|heic)$/i)) {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const base64String = event.target?.result as string;
      const img = new Image();
      img.src = base64String;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;
        
        const MAX_DIMENSION = 1800;
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          if (width > height) {
            height = Math.round((height * MAX_DIMENSION) / width);
            width = MAX_DIMENSION;
          } else {
            width = Math.round((width * MAX_DIMENSION) / height);
            height = MAX_DIMENSION;
          }
        }
        
        let quality = 0.85;
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        
        if (ctx) {
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
        }
        
        let dataUrl = canvas.toDataURL("image/jpeg", quality);
        const maxBase64Length = maxSizeMB * 1024 * 1024 * 1.33;
        
        while (dataUrl.length > maxBase64Length && quality > 0.2) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL("image/jpeg", quality);
        }
        
        resolve(dataUrl);
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
};
